import {UseQueryResult, useQuery} from '@tanstack/react-query'
import {SubscriptionDetailsType} from 'interfaces'
import {EndpointsService, SettingsService} from 'services/api'

export const SUBSCRIPTION_DETAILS_QUERY_KEY = 'getSubscriptionDetails'

/**
 * @description Data stays undefined until the first fetch succeeds, so callers
 * must handle it. Don't add initialData: Settings > General prefetches this
 * key, and initialData is ignored once the query already exists.
 */
export const useSubscriptionDetailsQuery =
  (): UseQueryResult<SubscriptionDetailsType> => {
    return useQuery({
      queryKey: [SUBSCRIPTION_DETAILS_QUERY_KEY],
      queryFn: subscriptionDetailsQueryFunction,
    })
  }

/**
 * @description Subscription details plus the count of licenses in use. The
 * in-use count comes from a separate endpoints call; if that call fails or has
 * no count, the subscription still loads and the count is left out.
 */
export const subscriptionDetailsQueryFunction =
  async (): Promise<SubscriptionDetailsType> => {
    const [subscriptionResult, aggregationResult] = await Promise.allSettled([
      SettingsService.getSubscriptionDetails(),
      EndpointsService.getEndpointMetaData(),
    ])

    if (subscriptionResult.status === 'rejected') {
      throw subscriptionResult.reason
    }

    const totalLicense =
      aggregationResult.status === 'fulfilled'
        ? aggregationResult.value.data?.totalLicense
        : undefined

    return {
      ...subscriptionResult.value.data,
      numberOfActiveLicenses:
        typeof totalLicense === 'number' ? String(totalLicense) : undefined,
    }
  }
